import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import _maxBy from 'lodash/maxBy';
import addDays from 'date-fns/addDays';

import { CheckSquareIcon, SquareIcon, SettingsIcon } from 'components/common/Icons';
import Tooltip from 'components/shared/Tooltip';
import { useCheckSensorStatus, useSensorList } from 'services/sensor';
import log from 'services/log';

import AiPredictionAlert from './AiPredictionAlert';
import {
  SensorToggleCell,
  InfoCell,
  InfoCellWrapper,
  SelectableRow,
  SmallNSubtle,
  SelectOnlyCell,
} from './common.styled';
import InTrainingIndication from './InTrainingIndication';

const SensorItem = ({ sensor, isSelected, hasAlert, onClick, onSelectOnly, onNavigate, disabled }) => {
  const navigate = useNavigate();
  const { sensorList } = useSensorList();
  const { sensorStatuses } = useCheckSensorStatus(sensorList);

  const displayLabels = sensor.labels?.filter((l) => l) || [];

  const sensorStatus = sensorStatuses.find((s) => s.sensorId === sensor.id);

  useEffect(() => {
    if (!sensorStatus) {
      log({
        message: 'Sensor status not found for sensor id',
        type: 'warning',
        origin: 'SensorSelector-SensorItem',
        info: {
          sensorId: sensor.id,
        },
      });
    }
  }, [sensor, sensorStatus]);

  const isOperational = sensorStatus?.operationalStatus === true;
  const inTraining =
    sensorStatus?.aiOperatorState.reduce((acc, operator) => {
      if (operator.state === 'learning') {
        acc.push(operator);
      }
      return acc;
    }, []) || [];
  const isInTraining = inTraining.length > 0;
  const informedDate = addDays(_maxBy(inTraining, 'createdAt')?.createdAt, 7);

  const handleClick = (e) => {
    if (!disabled) {
      onClick(e);
    }
  };

  const handleSelectOnly = (e) => {
    e.stopPropagation();
    if (!disabled) {
      onSelectOnly(sensor.id);
    }
  };

  const handleOpenSettings = (e) => {
    e.stopPropagation();
    navigate(`/settings/sensors/${sensor.id}`);
    onNavigate && onNavigate();
  };

  return (
    <SelectableRow
      key={`sensor-${sensor.id}`}
      onClick={handleClick}
      data-test-id={`select-sensor-${sensor.name}`}
      data-test-selected={isSelected}
      $disabled={disabled}
    >
      <SensorToggleCell className="sensor" $selected={isSelected} $disabled={disabled}>
        {isSelected ? <CheckSquareIcon /> : <SquareIcon />}
      </SensorToggleCell>

      <InfoCell $selected={isSelected} $disabled={disabled}>
        <InfoCellWrapper $selected={isSelected} $color={sensor.color} $disabled={disabled}>
          <div className="leading-relaxed font-semibold">{sensor.name}</div>
          {sensor.description && <SmallNSubtle>{sensor.description}</SmallNSubtle>}
        </InfoCellWrapper>
      </InfoCell>
      <SmallNSubtle as="td" className="pt-1 old-leading-xtight font-semibold">
        {displayLabels.length > 0 && <>{displayLabels.join(', ')}</>}
      </SmallNSubtle>
      <td>
        {hasAlert && <AiPredictionAlert />}
        {!hasAlert && !isOperational && <div className="color-alert">No data</div>}
      </td>
      <td>{isInTraining && <InTrainingIndication informedDate={informedDate} />}</td>
      <td className="text-right">{sensor.mode}</td>
      <td width="30px" className="text-right">
        <Tooltip content={`Go to ${sensor.name} sensor settings`}>
          <button
            type="button"
            className="color-text-subtle hover:color-text-primary cursor-pointer align-middle"
            onClick={handleOpenSettings}
            aria-label={`Open settings for sensor ${sensor.name}`}
            data-test-id={`open-sensor-settings-${sensor.name}`}
          >
            <SettingsIcon className="size-3" />
          </button>
        </Tooltip>
      </td>
      <SelectOnlyCell width="30px" onClick={handleSelectOnly}>
        Only
      </SelectOnlyCell>
    </SelectableRow>
  );
};

export default React.memo(SensorItem);
